import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Col, Select } from 'antd';
import PropTypes from 'prop-types';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import { getIdMap, camelToTitle } from '../../helpers/helpers';

export default function ApprovalControls({
  formTemplateId,
  approvedOnly,
  onApprovedChanged,
  requiredApprovalStepId,
  onRequiredApprovalSelected,
}) {
  const formTemplates = useSelector((s) => s.forms.templates);
  const users = useSelector((s) => s.users?.users ?? []);
  const positionNames = useSelector((s) => s.settings?.positionNames ?? []);

  const positionsById = useMemo(() => getIdMap(positionNames), [positionNames]);
  const usersById = useMemo(() => getIdMap(users), [users]);
  const buildFieldLabelMap = (template) => {
    const map = {};
    (template?.sections ?? []).forEach((section) => {
      (section?.fields ?? []).forEach((f = {}) => {
        map[f.id] = f.configProps?.title ?? f.label ?? f.name;
      });
    });
    return map;
  };

  const fieldLabelMap = useMemo(() => {
    const tpl = formTemplates?.[formTemplateId];
    if (!tpl?.jsonFormData) return {};
    try {
      return buildFieldLabelMap(JSON.parse(tpl.jsonFormData));
    } catch {
      return {};
    }
  }, [formTemplateId, formTemplates]);

  const getConfiguredLabel = useCallback(
    (n, idx) => {
      const id = n.data?.users?.[0];
      if (!id) return `Approval step ${idx + 1}`;
      if (positionsById[id]) return `Approval Step for Any ${positionsById[id].name}`;
      if (usersById[id]) return `Approval Step for ${usersById[id].name}`;
      if (fieldLabelMap[id]) return `Approval Step for ${fieldLabelMap[id]}`;
      const title = camelToTitle(id);
      return title === id ? `Approval step ${idx + 1}` : `Approval Step for ${title}`;
    },
    [positionsById, usersById, fieldLabelMap],
  );

  const approvalLayers = useMemo(() => {
    const tpl = formTemplates?.[formTemplateId];
    if (!tpl?.jsonFormData) return [];
    try {
      const wf = JSON.parse(tpl.jsonFormData).workflow ?? {};
      const edges = wf.edges ?? [];
      const connected = new Set();
      edges.forEach(({
        from,
        to,
        source,
        target,
      }) => {
        if (from) connected.add(from);
        if (to) connected.add(to);
        if (source) connected.add(source);
        if (target) connected.add(target);
      });
      return (wf.nodes ?? [])
        .filter((n) => n.type === 'approval' && connected.has(n.id))
        .map((n, idx) => ({ id: n.id ?? String(idx), label: getConfiguredLabel(n, idx) }));
    } catch {
      return [];
    }
  }, [formTemplateId, formTemplates, getConfiguredLabel]);

  const ANY_APPROVAL = '';

  return (
    <>
      <Col>
        <OnTraccrCheckbox
          value={approvedOnly}
          onChange={onApprovedChanged}
          label="Approved Forms Only?"
        />
      </Col>
      {approvedOnly && (
        <Col>
          <Select
            placeholder="Any approval"
            style={{ width: 260 }}
            value={requiredApprovalStepId ?? ANY_APPROVAL}
            onChange={(v) => onRequiredApprovalSelected(v || null)}
            dropdownMatchSelectWidth={false}
            disabled={approvalLayers.length === 0}
            allowClear
          >
            <Select.Option key="any-approval" value={ANY_APPROVAL}>
              Any approval
            </Select.Option>
            {approvalLayers.map((l) => (
              <Select.Option key={l.id} value={l.id}>
                {l.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
      )}
    </>
  );
}

ApprovalControls.propTypes = {
  formTemplateId: PropTypes.string,
  approvedOnly: PropTypes.bool.isRequired,
  onApprovedChanged: PropTypes.func.isRequired,
  requiredApprovalStepId: PropTypes.string,
  onRequiredApprovalSelected: PropTypes.func.isRequired,
};

ApprovalControls.defaultProps = {
  formTemplateId: null,
  requiredApprovalStepId: null,
};
